/**
 * Public / Open API key + scope vocabulary. Browser-safe (shared with the
 * web key-management UI's scope picker) — no runtime dependencies.
 */

/**
 * Which surface a public API key authenticates against.
 * - `WORKSPACE` (`ti_ws_`): scoped to a single workspace; carries a stamped
 *   `wsRole`.
 * - `PERSONAL` (`ti_pk_`): acts as the creating user; role resolved per-request
 *   from live workspace membership.
 */
export enum ApiKeyType {
  WORKSPACE = 'workspace',
  PERSONAL = 'personal',
}

/**
 * Granular `resource:action` scopes an API key may be granted. A request is
 * authorized only if its required scope is present on the key AND the key's
 * (workspace) role permits the action. This is the curated set, mapping 1:1 to
 * the `/v2` endpoint surface (Accounts read + write, Warmup, Tags,
 * Analytics-read, Notifications). Add scopes here only when an endpoint needs
 * them — never speculatively.
 *
 * `ACCOUNTS_WRITE` gates the account-lifecycle routes (connect / disconnect /
 * delete) and is deliberately separate from `ACCOUNTS_READ`: a key that only
 * reads the mailbox list must not be able to delete one.
 */
export enum PublicApiScope {
  ACCOUNTS_READ = 'accounts:read',
  ACCOUNTS_WRITE = 'accounts:write',
  WARMUP_READ = 'warmup:read',
  WARMUP_MANAGE = 'warmup:manage',
  TAGS_READ = 'tags:read',
  TAGS_WRITE = 'tags:write',
  ANALYTICS_READ = 'analytics:read',
  NOTIFICATIONS_READ = 'notifications:read',
  NOTIFICATIONS_MANAGE = 'notifications:manage',
  /** Read-only: current plan, feature flags, effective limits, and usage vs limits. No write counterpart — plan changes only happen through the billing gateway's checkout (UI only). */
  BILLING_READ = 'billing:read',
  /**
   * Read-only: the workspace record itself plus its member roster. Also what a
   * workspace (`ti_ws_`) key needs for `GET /v2/workspaces`, which answers it
   * with its own workspace only (`@OrgScoped({ workspaceKeyScope })`).
   */
  WORKSPACE_READ = 'workspace:read',
  /**
   * Rename the workspace and manage its people (invite / change role / remove).
   *
   * Deliberately ONE scope rather than a settings/members split: both actions
   * are gated on the same workspace ADMIN in `SettingsService`, so splitting
   * them would advertise a distinction the authorization layer does not make.
   * Note it is `workspace:manage`, not `workspace:write` — it does NOT create or
   * delete workspaces (those stay UI-only: creation is org-role gated and
   * deletion cascades into member anonymization).
   */
  WORKSPACE_MANAGE = 'workspace:manage',
  /**
   * **Create** a workspace, and list the organization's workspaces.
   *
   * A separate scope rather than a widening of `WORKSPACE_MANAGE`, whose own
   * comment above states it does not create — silently changing that would grant
   * provisioning to every key already issued with `workspace:manage`.
   *
   * It is also the only ORG-scoped scope: its routes carry no `:workspaceId`, so
   * they run behind `@OrgScoped()`, which requires a PERSONAL key whose creator
   * is a genuine org OWNER. (`GET /v2/workspaces` additionally admits a
   * workspace key under `workspace:read`, answering with its own workspace only —
   * this scope is never what a workspace key is asked for.) Deletion is
   * deliberately still absent — it cascades into member teardown and is a
   * separate decision.
   */
  WORKSPACE_PROVISION = 'workspace:provision',
  /**
   * Read and reallocate the organization's per-workspace daily warmup budgets
   * (`GET`/`PATCH /v2/workspaces/quotas`), and set a budget when creating a
   * workspace (`dailyWarmupBudget` on `POST /v2/workspaces`).
   *
   * ORG-scoped like `WORKSPACE_PROVISION` — a budget is carved out of the org's
   * `aggregateDailyWarmup` cap, so moving it is an owner decision that affects
   * every workspace, never one workspace's own setting (which is why it is not
   * a field on the per-workspace `PATCH`, where a workspace admin could raise
   * their own share). A separate scope rather than a widening of
   * `WORKSPACE_PROVISION`, for the same reason provision was split from
   * `WORKSPACE_MANAGE`: widening would silently grant budget control to every
   * provisioning key already issued.
   */
  WORKSPACE_QUOTA = 'workspace:quota',
}
